import { useEffect, useRef, useState } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Close";

/**
 * InlineFilter — the search + result-count strip Directory list
 * pages hand-roll today.
 *
 * Consolidates:
 *  - Search TextField with SearchIcon + Clear button
 *  - Any number of extra filter chips (Select / ToggleButtonGroup /
 *    secondary TextFields) passed through as `children`
 *  - Right-aligned "X of Y" result count
 *
 * Search is debounced by 250ms — parents receive the settled value
 * via `onChange`, not on every keystroke. Consumers still control
 * the string state so re-renders don't wipe the field.
 *
 * Props:
 *   value        : current search string
 *   onChange     : (next) => void — called after debounce
 *   placeholder  : search input placeholder
 *   children     : extra filter controls placed next to the search
 *   count        : { shown: n, total: m, noun: "user" }  (renders "N of M user(s)")
 *   debounceMs   : override debounce (default 250)
 */
export default function InlineFilter({
  value = "",
  onChange,
  placeholder = "Filter",
  children,
  count,
  debounceMs = 250,
  sx,
}) {
  const [local, setLocal] = useState(value);
  const timer = useRef(null);

  // Keep local in sync when the parent resets the value externally.
  useEffect(() => {
    setLocal(value);
  }, [value]);

  const push = (next) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (typeof onChange === "function") onChange(next);
    }, debounceMs);
  };

  const clear = () => {
    setLocal("");
    if (timer.current) clearTimeout(timer.current);
    if (typeof onChange === "function") onChange("");
  };

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={2}
      alignItems={{ sm: "center" }}
      sx={{ mb: 2, flexWrap: "wrap", ...sx }}
      useFlexGap
    >
      <TextField
        size="small"
        placeholder={placeholder}
        value={local}
        onChange={(e) => {
          setLocal(e.target.value);
          push(e.target.value);
        }}
        sx={{ maxWidth: 360, flex: "1 1 260px" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: local ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={clear}
                aria-label="Clear search"
                edge="end"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
      {children}
      {count && (
        <Box sx={{ ml: { sm: "auto" } }}>
          <Typography variant="caption" color="text.secondary">
            {count.shown} of {count.total} {count.noun}
            {count.total === 1 || !count.noun ? "" : "s"}
          </Typography>
        </Box>
      )}
    </Stack>
  );
}
